import { useEffect, useState } from 'react'
import { api, type GoldenCase } from '../lib/api.ts'
import { Button } from '../components/Button.tsx'
import { useConfirm } from '../components/Confirm.tsx'
import { icons } from '../components/icons.ts'
import { useToast } from '../components/Toast.tsx'
import { GoldenSetTabs } from '../components/GoldenSetTabs.tsx'

/**
 * The golden set, listed.
 *
 * Nine labelled prescriptions: what the reader is measured against. The point
 * of the list is the two columns nobody can see from the files -- how much of
 * a case has regions drawn, and whether a human has confirmed it -- because
 * those are what decide whether a case is worth anything yet.
 *
 * Internal, and development-only by construction: the routes behind it read
 * the working tree, so a deployed instance has nothing to serve.
 */

const WRITING: Readonly<Record<string, string>> = {
  handwritten: 'Manuscrita',
  printed: 'Impressa',
  mixed: 'Mista',
}

export function GoldenSet({
  onOpenCase,
  onBack,
}: {
  onOpenCase: (caseId: string) => void
  onBack: () => void
}) {
  const [cases, setCases] = useState<GoldenCase[] | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [adding, setAdding] = useState(false)
  const [newId, setNewId] = useState('')
  const [file, setFile] = useState<File | undefined>()
  const [busy, setBusy] = useState(false)

  const [removing, setRemoving] = useState<string | undefined>()
  const confirm = useConfirm()
  const toast = useToast()

  function load() {
    void api
      .goldenSet()
      .then((r) => setCases(r.cases))
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
  }

  useEffect(load, [])

  /**
   * Removes a case, once somebody has said so in as many words.
   *
   * A case is an answer key: removing one discards the work somebody did to
   * decide what "correct" means, along with the receita pages beside it. The
   * dialog is the product's one way of asking, so this asks the same way
   * everything else destructive does.
   */
  async function remove(caseId: string) {
    const sure = await confirm({
      title: `Remover ${caseId}?`,
      message: 'O gabarito e as páginas saem do repositório e do bucket. Não dá para desfazer.',
      confirmLabel: 'Remover',
      tone: 'danger',
    })
    if (!sure) return

    setRemoving(caseId)
    try {
      await api.deleteGoldenCase(caseId)
      toast.show(`${caseId} removido`, { tone: 'success' })
      load()
    } catch (cause) {
      toast.show('Não foi possível remover o caso', {
        tone: 'error',
        detail: cause instanceof Error ? cause.message : undefined,
      })
    } finally {
      setRemoving(undefined)
    }
  }

  /**
   * Adds a case, then opens it.
   *
   * The label arrives empty on purpose, so the only useful next act is to fill
   * it in against the image — which is the screen this hands over to.
   */
  async function add() {
    if (file === undefined || newId.trim() === '') return
    setBusy(true)
    setError(undefined)
    try {
      const created = await api.createGoldenCase(newId.trim(), file)
      setAdding(false)
      setNewId('')
      setFile(undefined)
      load()
      onOpenCase(created.created)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao criar')
    } finally {
      setBusy(false)
    }
  }

  const corrected = (cases ?? []).filter((c) => c.status === 'corrected').length

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Fila
        </Button>
        <h1 className="text-tH4 text-text-strong">Conjunto de referência</h1>
        <span className="bg-fill-brand-weak text-text-brand rounded-base text-tTiny px-2 py-0.5 font-semibold">
          interno
        </span>
      </div>

      <GoldenSetTabs active="reading" />

      <p className="text-tP text-text-weak">
        As receitas que servem de gabarito: o que cada uma deveria ser lida como. Nada é medido
        contra um rascunho — um caso só conta depois que alguém confere o que está escrito aqui
        contra a imagem.
      </p>

      {/*
        Adding a case is uploading its page. Everything else about a case is
        the label, and the label is what the review screen is for.
      */}
      <div className="flex flex-wrap items-end gap-3">
        {!adding && (
          <Button variant="secondary" tone="neutral" size="small" onClick={() => setAdding(true)}>
            Adicionar caso
          </Button>
        )}
        {adding && (
          <>
            <label className="flex flex-col gap-1">
              <span className="text-tTiny text-text-weak font-semibold uppercase">
                Identificador
              </span>
              <input
                value={newId}
                onChange={(event) => setNewId(event.target.value)}
                placeholder="manuscrita-04"
                className="rounded-base border-stroke-weak bg-background-raised text-tP text-text-strong border px-3 py-2"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-tTiny text-text-weak font-semibold uppercase">Imagem</span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(event) => setFile(event.target.files?.[0])}
                className="text-tP text-text-weak"
              />
            </label>
            <Button
              variant="primary"
              tone="brand"
              size="small"
              disabled={busy || file === undefined || newId.trim() === ''}
              onClick={() => void add()}
            >
              {busy ? 'Enviando…' : 'Criar'}
            </Button>
            <Button
              variant="tertiary"
              tone="neutral"
              size="small"
              onClick={() => {
                setAdding(false)
                setNewId('')
                setFile(undefined)
              }}
            >
              Cancelar
            </Button>
          </>
        )}
      </div>

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}
      {cases === undefined && error === undefined && (
        <p className="text-tP text-text-weak" role="status" aria-live="polite">
          Carregando…
        </p>
      )}

      {cases !== undefined && (
        <>
          <p className="text-tSmall text-text-weak">
            {cases.length} casos · {corrected} conferido(s)
          </p>

          <div className="rounded-base border-stroke-weak bg-background-raised w-full overflow-x-auto border">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {['Caso', 'Escrita', 'Preparações', 'Ativos', 'Regiões', 'Situação', ''].map(
                    (h) => (
                      <th
                        key={h === '' ? 'actions' : h}
                        scope="col"
                        className="border-stroke-weak bg-background-sunken text-tPSemi text-text-strong border-b px-3 py-3 text-left"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {cases.map((row) => (
                  <tr
                    key={row.caseId}
                    tabIndex={0}
                    role="button"
                    className="hover:bg-fill-weak/40 cursor-pointer"
                    onClick={() => onOpenCase(row.caseId)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      onOpenCase(row.caseId)
                    }}
                  >
                    <td className="border-stroke-weak text-tP text-text-strong border-b px-3 py-3">
                      {row.caseId}
                    </td>
                    <td className="border-stroke-weak text-tP text-text-weak border-b px-3 py-3">
                      {WRITING[row.writing] ?? row.writing}
                    </td>
                    <td className="border-stroke-weak text-tP text-text-weak border-b px-3 py-3 tabular-nums">
                      {row.preparations}
                    </td>
                    <td className="border-stroke-weak text-tP text-text-weak border-b px-3 py-3 tabular-nums">
                      {row.ingredients}
                    </td>
                    {/*
                      The column that says whether citation accuracy can be
                      measured for this case at all. Zero is not a warning --
                      it is simply work not done yet.
                    */}
                    <td className="border-stroke-weak text-tP border-b px-3 py-3 tabular-nums">
                      <span className={row.boxed > 0 ? 'text-text-brand' : 'text-text-weak'}>
                        {row.boxed > 0 ? row.boxed : '—'}
                      </span>
                    </td>
                    <td className="border-stroke-weak text-tP border-b px-3 py-3">
                      <span
                        className={
                          row.status === 'corrected' ? 'text-text-brand' : 'text-text-weak'
                        }
                      >
                        {row.status === 'corrected' ? 'Conferido' : 'Rascunho'}
                      </span>
                    </td>
                    {/*
                      Removing lives here rather than inside the case.

                      It is a decision about *which* case, and that is a
                      question you answer looking at the list -- from inside
                      one, "remove" is the only button on the screen that does
                      not act on what you are reading.

                      The trash on its own, as everywhere else that offers
                      one -- tertiary, danger, `size="small"`. Not
                      `size="icon"`: that is the one size the button sets no
                      icon scale for, so the glyph came out a different size
                      from every other icon on the screen.
                      `stopPropagation` because the row itself opens the case.
                    */}
                    <td className="border-stroke-weak border-b px-3 py-3 text-right">
                      <Button
                        variant="tertiary"
                        tone="danger"
                        size="small"
                        leadingIcon={icons.remove}
                        aria-label={`Remover ${row.caseId}`}
                        title="Remover este caso"
                        disabled={removing === row.caseId}
                        onClick={(event) => {
                          event.stopPropagation()
                          void remove(row.caseId)
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
